/**
 * THE MANIFEST CONTEXT — one resolved product, read by the header, the toolbar and the canvas.
 *
 * <p>A context rather than a prop chain for the reason `glassTransparency` is a store: three
 * consumers in three component files, two of them rendered from different parents. Threading a
 * prop would be three signatures changed and three chances to forget one, and a surface left on
 * the previous product is exactly the divergence this architecture exists to remove.
 *
 * <h2>⚠ THE PREVIEW IS THE EXISTING ONE, NOT A SECOND MECHANISM</h2>
 *
 * The brief asks for `setPreviewManifest(manifestId)` for live admin preview. That already
 * exists, under another name: `productTarget.js` holds `PRODUCT_OVERRIDE_STORAGE_KEY`,
 * `readProductOverride(isAdmin)` and `writeProductOverride`, and `AdminProductSwitcher` is the
 * dropdown in the Admin Panel that drives them.
 *
 * <p>So this delegates rather than inventing a key of its own. A second store would be two admin
 * previews over one question: an operator flips the existing switcher and the manifest does not
 * follow, or flips this one and the deny lists do not — which is precisely the "four gates
 * answering in four places" defect the manifests replace, reintroduced by the file that replaces
 * it. `setPreviewManifest` is therefore a thin, correctly-named front door onto the mechanism
 * that is already tested and already role-gated.
 *
 * <p>Two properties come along for free, and both are load-bearing:
 * <ul>
 *   <li>⚠ `readProductOverride` is UNREADABLE by a non-admin, not merely hidden from one — it
 *       takes the live role and returns the build's own target for anything falsy. A customer
 *       cannot grant themselves the Studio by writing one key into their own localStorage;</li>
 *   <li>it FAILS CLOSED — storage unavailable, or holding junk, resolves to the committed target
 *       and never to the most permissive of the four.</li>
 * </ul>
 */

import React, {
  createContext, useContext, useMemo, useState, useCallback,
} from 'react';
import PropTypes from 'prop-types';
import {
  MANIFESTS, MANIFEST_IDS, resolveInitialManifest, astroManifest,
} from '../config/manifests';
import {
  readProductOverride, writeProductOverride, PRODUCT_OVERRIDE_STORAGE_KEY,
} from '../config/productTarget';

/** Re-exported so a consumer of the context needs one import. Part of the contract. */
export { PRODUCT_OVERRIDE_STORAGE_KEY };

/**
 * The stored preview, or null.
 *
 * <p>Delegates to `readProductOverride`, which is role-gated: a falsy `isAdmin` returns the
 * build's own target, which is NOT a preview, so this reports null for it. The distinction
 * matters — the dropdown shows "no override" rather than the target's own name.
 */
export const readStoredPreview = (isAdmin) => {
  if (!isAdmin) return null;
  let raw = null;
  try {
    raw = window.localStorage.getItem(PRODUCT_OVERRIDE_STORAGE_KEY);
  } catch (err) {
    return null;
  }
  const resolved = readProductOverride(true);
  /*
   * ⚠ Only a value actually PRESENT in storage counts as a preview. `readProductOverride`
   * returns the build's target when the key is absent, and reporting that as a preview would
   * make the dropdown claim an override nobody set.
   */
  return raw && MANIFESTS[resolved] ? resolved : null;
};

/**
 * ⚠ THE DEFAULT IS A REAL MANIFEST, never null or undefined.
 *
 * Every consumer reads `manifest.brandName`, `manifest.watermark` and `manifest.toolbar`
 * unconditionally. A null default makes each of those a crash on the one path nobody tests — a
 * component rendered outside the provider, which in this suite is most of them. Astro is the
 * same fallback `resolveInitialManifest` lands on.
 */
export const ManifestContext = createContext({
  manifest: astroManifest,
  previewId: null,
  setPreviewManifest: () => {},
});

export const ManifestProvider = ({ hostname, isAdmin, children }) => {
  /*
   * The RAW stored value is held in state and the effective one derived through
   * `resolveInitialManifest`, which is the arrangement `readProductOverride`'s own note asks for:
   * a demoted administrator loses the override on the render their role changes, with no effect
   * to fire and nothing to invalidate.
   */
  const [previewId, setPreviewId] = useState(() => readStoredPreview(isAdmin));

  const manifest = useMemo(
    () => resolveInitialManifest(hostname, isAdmin, previewId),
    [hostname, isAdmin, previewId],
  );

  const setPreviewManifest = useCallback((id) => {
    /*
     * An unknown id CLEARS the preview rather than being ignored. The caller is a dropdown and
     * "no override" is one of its options, so null has to mean something — and a typo resolving
     * to the previous preview would be a control that silently did nothing. Matches
     * `writeProductOverride`, which also clears rather than storing junk.
     */
    const next = id && MANIFESTS[id] ? id : null;
    setPreviewId(next);
    writeProductOverride(next);
  }, []);

  const value = useMemo(
    () => ({ manifest, previewId, setPreviewManifest }),
    [manifest, previewId, setPreviewManifest],
  );

  return (
    <ManifestContext.Provider value={value}>
      {children}
    </ManifestContext.Provider>
  );
};

ManifestProvider.propTypes = {
  hostname: PropTypes.string,
  isAdmin: PropTypes.bool,
  children: PropTypes.node,
};

ManifestProvider.defaultProps = {
  hostname: typeof window !== 'undefined' ? window.location.hostname : '',
  isAdmin: false,
  children: null,
};

/** The resolved manifest. Safe outside a provider — see the default above. */
export const useManifest = () => useContext(ManifestContext).manifest;

/** The whole context, for the admin preview dropdown. */
export const useManifestContext = () => useContext(ManifestContext);

/** The ids a preview dropdown may offer. */
export const PREVIEWABLE_MANIFEST_IDS = MANIFEST_IDS;

export default ManifestContext;
